'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getPrescriptionsByCompany,
    Prescription,
} from '@/lib/api/prescriptions/prescription-api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdSearch,
    MdClose,
    MdArrowForward,
    MdAdd,
    MdDescription,
    MdPerson,
    MdAccessTime,
    MdMedication,
    MdFileDownload,
} from 'react-icons/md'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'

export default function PrescriptionsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('medical', 'prescriptions')
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showImportModal, setShowImportModal] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch],
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getPrescriptionsByCompany(user!.companyId!, params)
        },
        [user?.companyId],
    )

    const {
        items: displayedPrescriptions,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Prescription, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    if (loading && displayedPrescriptions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando receitas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Receituário
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1 ? 'receita' : 'receitas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar receitas..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    handleClearSearch()
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <div className="flex items-center gap-2">
                                    <button
                                        className="btn btn-outline btn-secondary gap-2"
                                        onClick={() => setShowImportModal(true)}
                                    >
                                        <MdFileDownload className="text-xl" />
                                        <span className="hidden sm:inline">Importar</span>
                                    </button>
                                    <button
                                        onClick={() => navigate('/medical/prescriptions/new')}
                                        className="btn btn-secondary gap-2"
                                    >
                                        <MdAdd className="text-lg" />
                                        <span className="hidden sm:inline">
                                            Nova Receita
                                        </span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {displayedPrescriptions.length === 0 && !loading ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdDescription className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma receita encontrada
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Comece adicionando sua primeira receita
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/medical/prescriptions/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Receita
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {displayedPrescriptions.map(prescription => (
                            <div
                                key={prescription.id}
                                onClick={() =>
                                    navigate(`/medical/prescriptions/${prescription.id}`)
                                }
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        {prescription.client && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70 mb-2">
                                                <MdPerson className="text-base" />
                                                <span className="font-semibold truncate">
                                                    {prescription.client.name}
                                                </span>
                                            </div>
                                        )}
                                        <p className="text-sm text-base-content/60 line-clamp-2">
                                            {prescription.content}
                                        </p>
                                    </div>
                                    <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                                </div>

                                {prescription.items &&
                                    prescription.items.length > 0 && (
                                        <div className="flex items-center gap-2 mb-3">
                                            <MdMedication className="text-green-500" />
                                            <span className="text-sm text-base-content/70">
                                                {prescription.items.length}{' '}
                                                medicamento
                                                {prescription.items.length > 1
                                                    ? 's'
                                                    : ''}
                                            </span>
                                        </div>
                                    )}

                                <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                    <div className="flex items-center gap-2 text-xs text-base-content/60">
                                        <MdAccessTime className="text-sm" />
                                        <span>
                                            {format(
                                                new Date(
                                                    prescription.prescriptionDate
                                                ),
                                                'dd/MM/yyyy',
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    </div>
                                    {prescription.validUntil && (
                                        <span className="badge badge-sm badge-ghost">
                                            Valida ate{' '}
                                            {format(
                                                new Date(
                                                    prescription.validUntil
                                                ),
                                                'dd/MM',
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="receita"
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PRESCRIPTION}
                moduleLabel="Receitas"
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getPrescriptionsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Prescription) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.client?.name || `Receita #${item.id}`}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {item.content}
                        </p>
                        {item.items && item.items.length > 0 && (
                            <span className="badge badge-xs badge-ghost mt-1">
                                {item.items.length} medicamento{item.items.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                )}
                getItemId={(item: Prescription) => item.id}
                getItemName={(item: Prescription) =>
                    item.client?.name || `Receita #${item.id}`
                }
                onImportComplete={reload}
            />
        </div>
    )
}
